import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { orderCostSummary, lineMargin, marginTone, isCarriage } from '@/lib/costMargin';

const money = (v) => `£${Number(v).toFixed(2)}`;

function Stat({ label, value, sub, tone = 'text-white' }) {
  return (
    <div className="border border-white/5 bg-white/[0.02] px-2.5 py-2">
      <p className="text-white/30 text-[9px] font-bold tracking-widest">{label}</p>
      <p className={`font-black text-sm ${tone}`}>{value}</p>
      {sub && <p className={`text-[9px] ${tone}`}>{sub}</p>}
    </div>
  );
}

// COGS and gross margin for one sale, read from the cost snapshotted when the
// sale was made, with the per-line breakdown behind the totals.
export default function OrderMarginPanel({ order }) {
  const summary = orderCostSummary(order);
  const lines = (Array.isArray(order.line_items) ? order.line_items : []).filter((li) => !isCarriage(li));

  return (
    <div className="pt-3 mt-1 border-t border-white/5">
      <p className="text-primary text-[10px] font-bold tracking-widest mb-2">MARGIN</p>

      <div className="grid grid-cols-3 gap-2">
        <Stat label="NET" value={money(summary.net)} />
        <Stat label="COGS" value={summary.cogs == null ? '—' : money(summary.cogs)} />
        <Stat
          label="GROSS MARGIN"
          value={summary.margin == null ? '—' : money(summary.margin)}
          sub={summary.marginPct == null ? null : `${summary.marginPct.toFixed(0)}%`}
          tone={marginTone(summary.marginPct)}
        />
      </div>

      {summary.backfilled && (
        <p className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-bold text-cyan-400 bg-cyan-400/10 px-2 py-0.5">
          <AlertTriangle className="w-2.5 h-2.5" /> BACKFILLED ESTIMATE
        </p>
      )}
      {summary.missingCost && (
        <p className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-bold text-amber-400 bg-amber-400/10 px-2 py-0.5">
          <AlertTriangle className="w-2.5 h-2.5" /> NO COST RECORDED
        </p>
      )}
      {summary.partial && (
        <p className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-bold text-amber-400 bg-amber-400/10 px-2 py-0.5">
          <AlertTriangle className="w-2.5 h-2.5" /> PARTIAL COST
        </p>
      )}

      {lines.length > 0 && (
        <div className="mt-3 space-y-1">
          {lines.map((li, i) => {
            const m = lineMargin(li);
            return (
              <div key={i} className="flex items-baseline gap-2 text-[10px]">
                <span className="text-white/60 flex-1 min-w-0 truncate">{li.description}</span>
                <span className="text-white/30 shrink-0">×{li.qty}</span>
                <span className="text-white/40 shrink-0 w-14 text-right">{m.cost == null ? 'no cost' : money(m.cost)}</span>
                <span className={`shrink-0 w-14 text-right ${marginTone(m.margin == null || m.net <= 0 ? null : (m.margin / m.net) * 100)}`}>
                  {m.margin == null ? '—' : money(m.margin)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}